import { LineChart } from "@get-dx/d3-charts";
import {
  setQueryStringParam,
  deleteQueryStringParam,
  objectToQueryString,
} from "../../shared/utils";
import { getQueryParam } from "../../lib/utils";
import { scrubHtml } from "@/lib/utils";
import { chartTooltipHtml } from "../chartTooltipHtml";
import { teamTreeExpansionMixin } from "../mixins/teamTreeExpansion";
import tippy from "tippy.js";

export function snapshotAttrition({ url, canViewActuals }) {
  return {
    // Include team tree expansion functionality
    ...teamTreeExpansionMixin(),

    // Configure mixin id fields for this view (required for teamTreeExpansionMixin)
    rowIdKey: "snapshot_squad_id",
    cachedIdKey: "id",
    cachedParentKey: "parent_id",

    attritionModalOpen: false,
    url,
    attritionTabs: [{ tab: "risk" }],
    attritionData: null,
    drilldownData: {}, // Store breakdown data in drilldownData structure for mixin
    tabDataLoaded: false,
    tableSort: { col: "group_name", dir: "asc" },
    selectedAttritionTab: "",
    selectedAttritionPeriod: "6mo",
    selectedAttritionInterval: "mo",
    init() {
      const result = getQueryParam("attrition");
      const tab = getQueryParam("adt");
      const period = getQueryParam("adp");
      const interval = getQueryParam("adi");

      if (canViewActuals) this.attritionTabs.push({ tab: "actuals" });

      if (period) this.setPeriod(period);
      if (interval) this.setInterval(interval);

      if (result) this.showAttrition();
      if (tab) this.setTab(tab);
      else this.setTab(this.attritionTabs[0]?.tab);

      this.fetchBreakdownData();
      this.fetchAttritionData();

      this.$watch(
        () => this.$store.snapshotResultFilters,
        () => {
          this.fetchBreakdownData();
          this.fetchAttritionData();
        },
      );
    },
    tooltipIfTruncated(e, fullText) {
      const el = e.target;
      el._tippy?.destroy(); // Delete previous instance

      if (el && el.offsetWidth < el.scrollWidth) {
        tippy(el, {
          content: scrubHtml(fullText),
          delay: [250, 0],
          onShow: (instance) => {
            tippy.hideAll({ exclude: instance });
          },
        });
      }
    },
    // Get breakdown items using teamTreeExpansionMixin's method
    get breakdownItems() {
      // Ensure we have data before calling getBreakdownItems
      const resultItems =
        this.drilldownData?.team_breakdown?.data?.result_items;
      if (!resultItems || !Array.isArray(resultItems)) {
        return [];
      }

      return this.getBreakdownItems({
        directData: resultItems,
        sortBy: this.tableSort.col,
        reverse: this.tableSort.dir === "desc",
      });
    },

    get sortedBreakdown() {
      // Keep this for backward compatibility, but redirect to breakdownItems
      return this.breakdownItems;
    },
    get groupTitle() {
      // Check if we're in attribute mode
      const aggregation = this.$store?.snapshotResultFilters?.aggregation;
      if (aggregation?.startsWith("attribute_")) {
        return "Attribute";
      }

      return this.sortedBreakdown[0]?.group_title || "Team";
    },
    sortableHeader(column) {
      const isCurrentSort = column === this.tableSort.col;
      const isAsc = this.tableSort.dir === "asc";

      return `
        <div class="text-gray-300 flex flex-col -space-y-1.5 text-xs -mb-px">
          <i class="-mb-px bx bx-caret-up" :class="{'text-gray-500': ${
            isCurrentSort && isAsc
          }}"></i>
          <i class="bx bx-caret-down" :class="{'text-gray-500': ${
            isCurrentSort && !isAsc
          }}"></i>
        </div>
      `;
    },
    // Provide a custom sort function so the mixin uses our comparator instead of the tableSort fallback
    getSortFunction() {
      const col = this.tableSort?.col;
      const reverse = this.tableSort?.dir === "desc";

      if (col === "risk_percentage") {
        return (a, b) => {
          const toNumeric = (v) =>
            v === "hidden" || v === null || v === undefined ? null : Number(v);
          const av = toNumeric(a?.risk_percentage);
          const bv = toNumeric(b?.risk_percentage);

          if (av == null && bv == null) return 0;
          if (av == null) return 1;
          if (bv == null) return -1;

          return reverse ? bv - av : av - bv;
        };
      }

      if (col === "group_name") {
        return (a, b) => {
          const aName = (
            a?.snapshot_squad_name ||
            a?.group_name ||
            ""
          ).toLowerCase();
          const bName = (
            b?.snapshot_squad_name ||
            b?.group_name ||
            ""
          ).toLowerCase();
          const cmp = aName.localeCompare(bName);
          return reverse ? -cmp : cmp;
        };
      }

      // Let the mixin choose default behavior for other columns
      return null;
    },
    showAttrition() {
      this.attritionModalOpen = true;
      setQueryStringParam("attrition", true);
      this.fetchAttritionData();
    },
    hide() {
      deleteQueryStringParam("adt");
      deleteQueryStringParam("adp");
      deleteQueryStringParam("adi");
      deleteQueryStringParam("attrition");
      this.attritionModalOpen = false;
    },
    formatDate(d) {
      return new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
      }).format(d);
    },
    setTab(tab) {
      this.selectedAttritionTab = tab;
      this.refreshCharts(tab);
      setQueryStringParam("adt", tab);
    },
    setPeriod(period) {
      setQueryStringParam("adp", period);
      this.selectedAttritionPeriod = period;
      if (this.attritionData) this.fetchAttritionData();
    },
    setInterval(interval) {
      setQueryStringParam("adi", interval);
      this.selectedAttritionInterval = interval;
      if (this.attritionData) this.fetchAttritionData();
    },
    fetchAttritionData() {
      this.tabDataLoaded = false;
      setQueryStringParam("adt", this.selectedAttritionTab);
      if (!this.url) return;

      const fullUrl = `${this.url}${this.queryParams()}`;

      return fetch(fullUrl)
        .then((resp) => resp.json())
        .then((data) => {
          this.attritionData = data;
          this.tabDataLoaded = true;
          this.refreshCharts(this.selectedAttritionTab);
        });
    },
    queryParams() {
      const params = {
        p: this.selectedAttritionPeriod,
        i: this.selectedAttritionInterval,
        tag_ids: getQueryParam("tag_ids"),
        squad_ids: getQueryParam("squad_ids"),
        aggregation: this.$store.snapshotResultFilters.aggregation,
        atg: getQueryParam("atg"),
      };
      return `?${objectToQueryString(params)}`;
    },
    refreshCharts(tab) {
      if (this.attritionData?.chart_data.length > 0 && tab == "risk") {
        setTimeout(() => this.buildRiskChart(), 50);
      }
      if (this.attritionData?.data?.length > 0 && tab == "actuals") {
        setTimeout(() => this.buildActualsChart(), 50);
      }
    },
    buildRiskChart() {
      const chartData = this.attritionData.chart_data;
      const chartMax = Math.max(...chartData.map((obj) => obj.value)) || 0;

      const elChart = document.getElementById(`chartattr-risk-chart`);
      if (!elChart) return;

      elChart.innerHTML = null;

      if (chartData.length > 1) {
        const chartParams = {
          elChart,
          startDate: chartData[0].date,
          endDate: chartData[chartData.length - 1].date,
          yAxisMin: 0,
          yAxisMax: Math.round(chartMax * 1.2),
          tooltipHtml(d) {
            const params = {
              heading: "At-risk of attrition",
              d: {
                label: d.label,
                value: d.value,
                formattedValue: `${d.value}% of your organization`,
                contributorCount: d.contributor_count,
              },
            };

            return chartTooltipHtml.attritionRisk(params);
          },
          values: chartData,
        };

        new LineChart(chartParams);
      }
    },
    buildActualsChart() {
      const { data } = this.attritionData;
      if (!data) return;
      const chartMax = Math.max(...data.map((obj) => obj.value)) || 0;

      const elChart = document.getElementById(`chartattr-actuals-chart`);
      if (!elChart) return;
      elChart.innerHTML = null;

      const chartParams = {
        elChart,
        startDate: data[0].date,
        endDate: data[data.length - 1].date,
        yAxisMin: 0,
        yAxisMax: chartMax * 1.2,
        tooltipHtml(d) {
          const params = {
            heading: "Actuals",
            d: {
              label: d.label,
              value: d.value,
              formattedValue: d.value,
            },
          };

          return chartTooltipHtml.default(params);
        },
        values: data,
      };

      new LineChart(chartParams);
    },
    changeTableSort(column, defaultDir = "desc") {
      if (column !== this.tableSort.col) {
        this.tableSort.col = column;
        this.tableSort.dir = defaultDir;
      } else {
        this.tableSort.dir = this.tableSort.dir === "asc" ? "desc" : "asc";
      }
    },
    fetchBreakdownData() {
      const breakdownUrl = `${url}/breakdown${this.queryParams()}`;
      return fetch(breakdownUrl)
        .then((resp) => resp.json())
        .then((data) => {
          // Store in drilldownData structure for teamTreeExpansionMixin compatibility
          this.drilldownData.team_breakdown = {
            data: {
              result_items: data.result_items || [],
            },
          };
        })
        .catch((error) => {
          console.error("Error fetching breakdown data:", error);
        });
    },

    // Required methods for teamTreeExpansionMixin

    /**
     * Get the endpoint for fetching child team data
     * @param {Object} _parentRow - The parent team row being expanded
     * @returns {String} - API endpoint URL
     */
    // eslint-disable-next-line no-unused-vars
    getChildDataEndpoint(_parentRow) {
      return `${this.url}/breakdown`;
    },

    /**
     * Get the query parameters for fetching child team data
     * @param {Object} parentRow - The parent team row being expanded
     * @returns {Object} - Parameters object for the API call
     */
    getChildDataParams(parentRow) {
      const params = {
        p: this.selectedAttritionPeriod,
        i: this.selectedAttritionInterval,
        tag_ids: getQueryParam("tag_ids"),
        squad_ids: getQueryParam("squad_ids"),
        aggregation: "selected_teams", // Always use selected_teams for children
        parent_squad_id: parentRow.snapshot_squad_id,
        atg: getQueryParam("atg"),
      };
      return params;
    },

    /**
     * Get the cached squads data for parent/child relationship lookups
     * @returns {Array} - Array of squad objects with id, parent_id, is_parent properties
     */
    getCachedSquads() {
      // For attrition, we don't have a cached squads data structure like other components
      // The tree expansion will work by making server requests for children
      return [];
    },
  };
}
